// Helpers d'affichage client — source unique des conventions de format.
// Decision JJ 2026-05-03 (BUG-11 P0-04, P0-10).
//
// Avant : 4 calculs d'age et 8 formats de nom complet eparpilles dans
// l'UI (ClientDetail, AdminBlock, ClientInfoTab, DALForm,
// MobileDALWizard, OperationSection, plan financement, Zoho folder).
//
// Apres : tout passe par ces helpers. Pas de fallback hardcode "M.".
import { deriveDevisMode, DEVIS_MODES } from "./devisMode";
import { configLabel } from "../hooks/useConfigList";

/**
 * Calcule l'age d'un client.
 * Source de verite : champ `client.age` s'il est explicitement set
 * (ex: saisie terrain visiteur). Sinon derive depuis date_naissance ISO.
 * Retourne null si pas calculable.
 */
export function getClientAge(client) {
  if (!client) return null;
  if (typeof client.age === "number" && client.age > 0) return client.age;

  const dob = client.date_naissance;
  if (!dob) return null;
  const s = String(dob).trim();
  // Match ISO YYYY-MM-DD (avec ou sans timestamp apres)
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!iso) return null;
  const birth = new Date(`${iso[1]}-${iso[2]}-${iso[3]}T00:00:00`);
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  if (age < 0 || age > 130) return null;
  return age;
}

/**
 * Formate la date de naissance en JJ/MM/AAAA depuis ISO YYYY-MM-DD.
 * Tolere les formats DDMMYYYY (8 chiffres) et passe-through les autres.
 */
export function formatDateNaissance(value) {
  if (!value) return "-";
  const s = String(value).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const ddmm = s.match(/^(\d{2})(\d{2})(\d{4})$/);
  if (ddmm) return `${ddmm[1]}/${ddmm[2]}/${ddmm[3]}`;
  return s;
}

/**
 * Format unifie nom complet client : "Civilite Prenom NOM".
 * Options :
 *  - withCivilite (default true) : prefixe "M." / "Mme"
 *  - upperNom (default true) : nom en CAPS (convention ANAH/legal)
 *  - order ("prenom-nom" default) : "Prenom NOM" ou "NOM Prenom"
 *
 * Avant : 4 ordres differents (ClientDetail "civilite NOM PRENOM",
 * AdminBlock "civilite PRENOM NOM", DALForm "prenom NOM" sans civilite,
 * Zoho "NOM PRENOM" upper, plan financement "nom prenom" sans civilite).
 */
/**
 * Normalise un NOM (de famille) : tout en MAJUSCULES.
 * Convention JJ 2026-05-19 — affichage uniquement, DB inchangée.
 */
export function formatNom(value) {
  if (!value) return "";
  return String(value).trim().toUpperCase();
}

/**
 * Normalise un Prénom : 1ère lettre de chaque composant en MAJUSCULE,
 * reste en minuscules. Gère les prénoms composés (espace ou tiret) et
 * apostrophes (D'Artagnan → D'Artagnan).
 * Ex : "BRAHIM" → "Brahim", "JEAN-CHRISTOPHE" → "Jean-Christophe",
 *      "marie pierre" → "Marie Pierre".
 */
export function formatPrenom(value) {
  if (!value) return "";
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/(^|[\s\-'])([\p{L}])/gu, (_m, sep, c) => sep + c.toUpperCase());
}

export function formatClientFullName(client, opts = {}) {
  const {
    withCivilite = true,
    upperNom = true,
    order = "prenom-nom",
  } = opts;
  if (!client) return "";
  const civ = withCivilite ? (client.civilite || "").trim() : "";
  const nomRaw = (client.nom || "").trim();
  const prenomRaw = (client.prenom || "").trim();

  // Cas B2B : si raison_sociale prime
  if (
    client.type_client &&
    client.type_client !== "particulier" &&
    client.raison_sociale
  ) {
    return client.raison_sociale.trim();
  }

  const nomFmt = upperNom ? formatNom(nomRaw) : nomRaw;
  const prenomFmt = formatPrenom(prenomRaw);  // toujours capitalisé
  const parts = [];
  if (civ) parts.push(civ);
  if (order === "nom-prenom") {
    if (nomFmt) parts.push(nomFmt);
    if (prenomFmt) parts.push(prenomFmt);
  } else {
    if (prenomFmt) parts.push(prenomFmt);
    if (nomFmt) parts.push(nomFmt);
  }
  return parts.join(" ").replace(/\s+/g, " ").trim();
}

/**
 * Trim espaces ET ponctuation finale traînante (`,`, `;`).
 *
 * Source data fréquente : `client.adresse.rue = "65 Rue des Grisardes,"`
 * (virgule récupérée d'un export). Sans nettoyage, `formatAdresse`
 * joint avec `, ` → `"65 Rue des Grisardes,, 77140 Ville"`. On strip
 * ici pour couvrir tous les call sites (front + back symétriques).
 */
function _cleanPart(v) {
  let s = String(v ?? "").trim();
  while (s && (s.endsWith(",") || s.endsWith(";"))) s = s.slice(0, -1).trimEnd();
  return s;
}

/**
 * Format unifie adresse client en string lisible "rue, code_postal ville".
 * Tolere les 3 shapes legacy : objet Adresse, flat (code_postal/ville
 * a la racine), string directe.
 */
export function formatClientAddress(client) {
  if (!client) return "";
  const a = client.adresse;
  // String directe (legacy)
  if (typeof a === "string" && a.trim()) return _cleanPart(a);
  const isObj = a && typeof a === "object";
  const rue = (isObj && a.rue) || client.adresse_rue || "";
  const cp = (isObj && a.code_postal) || client.code_postal || "";
  const ville = (isObj && a.ville) || client.ville || "";
  const left = _cleanPart(rue);
  const right = [_cleanPart(cp), _cleanPart(ville)]
    .filter(Boolean)
    .join(" ");
  return [left, right].filter(Boolean).join(", ");
}

/**
 * Helper unifié — accepte n'importe quelle forme d'adresse et renvoie
 * la string canonique `"rue, code_postal ville"` (jamais d'objet, jamais
 * `[object Object]` côté JSX).
 *
 * Cas couverts :
 *  - string non vide → trim et retour direct
 *  - objet `{rue, code_postal, ville, complement?}` → concat
 *  - objet client `{adresse, adresse_rue, code_postal, ville}` → délégué
 *    à `formatClientAddress`
 *  - null / undefined / objet vide → string vide
 *
 * À utiliser PARTOUT où on rend une adresse (popup, planning, label,
 * description GCal, body SMS, PDF). Le bug TEF-DOS-2026-00251
 * (Carvalho) montre que les `||` shortcircuit laissent passer l'objet
 * et JSX appelle alors toString() → "[object Object]".
 */
export function formatAdresse(input) {
  if (!input) return "";
  if (typeof input === "string") return _cleanPart(input);
  if (typeof input !== "object") return _cleanPart(input);
  // Si l'objet ressemble à un client (a une propriété `adresse`),
  // déléguer au helper client qui gère la priorité legacy.
  if ("adresse" in input || "adresse_rue" in input) {
    const out = formatClientAddress(input);
    if (out) return out;
  }
  // Sinon, traiter comme objet adresse direct.
  const left = _cleanPart(input.rue);
  const cp = _cleanPart(input.code_postal || input.cp);
  const ville = _cleanPart(input.ville);
  const right = [cp, ville].filter(Boolean).join(" ");
  const base = [left, right].filter(Boolean).join(", ");
  const complement = _cleanPart(input.complement);
  if (complement) {
    return base ? `${base} (${complement})` : complement;
  }
  return base;
}

/**
 * GIR readable. Si client.gir absent ET age >= 70 → "Non requis"
 * (eligibilite MPA acquise par age — decision JJ 2026-05-03).
 */
export function formatGirOrNonRequis(client) {
  if (!client) return "-";
  if (client.gir) {
    return String(client.gir)
      .replace("gir_", "GIR ")
      .replace("non_evalue", "Non évalué");
  }
  const age = getClientAge(client);
  if (age !== null && age >= 70) return "Non requis";
  return "-";
}


/**
 * 🔴 « Personne morale ou non » — la règle UNIQUE (N°838/840, P-MARIO-01) :
 * celle du B2B (`devisMode` : professionnel, entreprise, copropriete, bailleur,
 * pro). Était recopiée en local dans ClientDetail ET CreateDossierCore.
 */
export function estPersonneMorale(type) {
  return deriveDevisMode({ type_client: type }) === DEVIS_MODES.B2B;
}

/**
 * Libellé du TYPE de client (badge) : la liste `client_types` DU TENANT fait foi
 * (Particulier / Entreprise / Syndic chez Mario) ; à défaut, le repli de la fiche
 * client (« Entreprise » pour une personne morale, sinon l'identifiant).
 */
export function libelleTypeClient(typesClient, type) {
  return configLabel(typesClient, type, estPersonneMorale(type) ? "Entreprise" : type);
}
